<script>
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import BaseModal from 'dashboard/components/dahreply/BaseModal.vue';
import { apiErrorMessage } from '../../../../api/coLiving/errors';
import { FIELD_CLASS, SELECT_CLASS } from '../constants';
import { SLA_TIERS } from '../catalogForms';
import {
  emptyRule,
  ruleToForm,
  validateRule,
  rulePayload,
  freeCategories,
} from '../routingForm';
import FormField from './FormField.vue';

export default {
  name: 'RuleModal',
  components: { BaseModal, FormField },
  props: {
    kind: {
      type: String,
      required: true,
      validator: value => ['default', 'override'].includes(value),
    },
    rule: { type: Object, default: null },
  },
  emits: ['close'],
  data() {
    return {
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      tiers: SLA_TIERS,
      form: this.rule ? ruleToForm(this.rule) : emptyRule(),
      errors: {},
      isSubmitting: false,
    };
  },
  computed: {
    ...mapGetters('coLivingCatalog', {
      activeRoles: 'getActiveRoles',
      activeCategories: 'getActiveCategories',
    }),
    ...mapGetters('coLivingCondos', { condos: 'getActiveCondos' }),
    ...mapGetters('coLivingRouting', { defaults: 'getDefaults' }),
    isEdit() {
      return !!this.rule;
    },
    isOverride() {
      return this.kind === 'override';
    },
    // A default is one per category, so a new one offers only the free ones.
    categoryOptions() {
      return this.isOverride
        ? this.activeCategories
        : freeCategories(this.activeCategories, this.defaults);
    },
  },
  methods: {
    ...mapActions('coLivingRouting', [
      'addDefault',
      'editDefault',
      'addOverride',
      'editOverride',
    ]),
    errorText(field) {
      return this.errors[field]
        ? this.$t(`CO_LIVING.ROUTING.ERRORS.${this.errors[field]}`)
        : '';
    },
    async onSubmit() {
      const opts = { kind: this.kind, isEdit: this.isEdit };
      this.errors = validateRule(this.form, opts);
      if (Object.keys(this.errors).length) return;
      this.isSubmitting = true;
      const fields = rulePayload(this.form, opts);
      try {
        if (this.isEdit) {
          const action = this.isOverride ? this.editOverride : this.editDefault;
          await action({ id: this.rule.id, fields });
        } else {
          const action = this.isOverride ? this.addOverride : this.addDefault;
          await action(fields);
        }
        useAlert(this.$t('CO_LIVING.ROUTING.SAVED'));
        this.$emit('close');
      } catch (err) {
        useAlert(apiErrorMessage(this, err, 'CO_LIVING.ROUTING.SAVE_FAILED'));
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template>
  <BaseModal @close="$emit('close')">
    <template #title>
      {{
        $t(
          `CO_LIVING.ROUTING.${isOverride ? 'OVERRIDE' : 'DEFAULT'}_${
            isEdit ? 'EDIT' : 'ADD'
          }`
        )
      }}
    </template>

    <form
      class="grid grid-cols-1 gap-4"
      data-testid="co-living-rule-form"
      @submit.prevent="onSubmit"
    >
      <FormField
        :label="$t('CO_LIVING.ROUTING.FIELDS.CATEGORY')"
        for-id="co-living-rule-category"
        :error="errorText('category')"
      >
        <select
          id="co-living-rule-category"
          v-model="form.category"
          :disabled="isEdit"
          :class="[selectClass, errors.category ? 'border-red-500' : '']"
          data-testid="co-living-rule-category"
        >
          <option value="" disabled>
            {{ $t('CO_LIVING.ROUTING.FIELDS.CATEGORY_PLACEHOLDER') }}
          </option>
          <option v-if="isEdit" :value="form.category">
            {{ rule.category_name }}
          </option>
          <option
            v-for="category in isEdit ? [] : categoryOptions"
            :key="category.key"
            :value="category.key"
          >
            {{ category.name }}
          </option>
        </select>
      </FormField>

      <FormField
        v-if="isOverride"
        :label="$t('CO_LIVING.ROUTING.FIELDS.CONDO')"
        for-id="co-living-rule-condo"
        :error="errorText('site_id')"
      >
        <select
          id="co-living-rule-condo"
          v-model="form.site_id"
          :disabled="isEdit"
          :class="[selectClass, errors.site_id ? 'border-red-500' : '']"
          data-testid="co-living-rule-condo"
        >
          <option value="" disabled>
            {{ $t('CO_LIVING.ROUTING.FIELDS.CONDO_PLACEHOLDER') }}
          </option>
          <option v-if="isEdit" :value="form.site_id">
            {{ rule.site_name }}
          </option>
          <option
            v-for="condo in isEdit ? [] : condos"
            :key="condo.id"
            :value="condo.id"
          >
            {{ condo.name }}
          </option>
        </select>
      </FormField>

      <FormField
        :label="$t('CO_LIVING.ROUTING.FIELDS.PRIMARY')"
        for-id="co-living-rule-primary"
        :error="errorText('primary_role')"
        :hint="$t('CO_LIVING.ROUTING.FIELDS.PRIMARY_HINT')"
      >
        <select
          id="co-living-rule-primary"
          v-model="form.primary_role"
          :class="[selectClass, errors.primary_role ? 'border-red-500' : '']"
          data-testid="co-living-rule-primary"
        >
          <option value="" disabled>
            {{ $t('CO_LIVING.ROUTING.FIELDS.ROLE_PLACEHOLDER') }}
          </option>
          <option v-for="role in activeRoles" :key="role.key" :value="role.key">
            {{ role.label }}
          </option>
        </select>
      </FormField>

      <FormField
        :label="$t('CO_LIVING.ROUTING.FIELDS.BACKUP')"
        for-id="co-living-rule-backup"
        :hint="
          $t(
            isOverride
              ? 'CO_LIVING.ROUTING.FIELDS.INHERIT_HINT'
              : 'CO_LIVING.ROUTING.FIELDS.BACKUP_HINT'
          )
        "
      >
        <select
          id="co-living-rule-backup"
          v-model="form.backup_role"
          :class="selectClass"
          data-testid="co-living-rule-backup"
        >
          <option value="">
            {{
              $t(
                isOverride
                  ? 'CO_LIVING.ROUTING.INHERIT'
                  : 'CO_LIVING.COMMON.NONE'
              )
            }}
          </option>
          <option v-for="role in activeRoles" :key="role.key" :value="role.key">
            {{ role.label }}
          </option>
        </select>
      </FormField>

      <FormField
        :label="$t('CO_LIVING.ROUTING.FIELDS.SUPERVISOR')"
        for-id="co-living-rule-supervisor"
        :hint="$t('CO_LIVING.ROUTING.FIELDS.SUPERVISOR_HINT')"
      >
        <select
          id="co-living-rule-supervisor"
          v-model="form.supervisor_role"
          :class="selectClass"
          data-testid="co-living-rule-supervisor"
        >
          <option value="">
            {{
              $t(
                isOverride
                  ? 'CO_LIVING.ROUTING.INHERIT'
                  : 'CO_LIVING.COMMON.NONE'
              )
            }}
          </option>
          <option v-for="role in activeRoles" :key="role.key" :value="role.key">
            {{ role.label }}
          </option>
        </select>
      </FormField>

      <FormField
        :label="$t('CO_LIVING.ROUTING.FIELDS.TIER')"
        for-id="co-living-rule-tier"
        :hint="$t('CO_LIVING.ROUTING.FIELDS.TIER_HINT')"
      >
        <select
          id="co-living-rule-tier"
          v-model="form.sla_tier"
          :class="selectClass"
          data-testid="co-living-rule-tier"
        >
          <option value="">
            {{ $t('CO_LIVING.ROUTING.TIER_FROM_CATEGORY') }}
          </option>
          <option v-for="tier in tiers" :key="tier" :value="tier">
            {{ tier }} {{ $t(`CO_LIVING.SLA.TIER_NAMES.${tier}`) }}
          </option>
        </select>
      </FormField>

      <div class="flex justify-end gap-3">
        <woot-button variant="clear" type="button" @click="$emit('close')">
          {{ $t('CO_LIVING.COMMON.CANCEL') }}
        </woot-button>
        <woot-button
          type="submit"
          :is-loading="isSubmitting"
          data-testid="co-living-rule-save"
        >
          {{ $t('CO_LIVING.COMMON.SAVE') }}
        </woot-button>
      </div>
    </form>
  </BaseModal>
</template>
